"use client";

import { ErrorView } from "@/components/error-view";
import {
  FinancialMetricsComparison,
  useFinancialMetricsComparison,
} from "@/features/analysis/services/get-financial-metrics-comparison";
import { Lang } from "@/i18n-config";
import { getTranslations } from "@/utils/i18n/get-translations";

import { LoadingIndicator } from "ui/core/button/LoadingIndicator";

import MetricListItem from "./MetricListItem";
import { metriceConfig } from "./data/metric-config";

export type MetricListItem = FinancialMetricsComparison["data"][number] & {
  unit: string;
};

const MetricListHeader = ({ lang }: { lang: Lang }) => {
  const { t } = getTranslations(lang);
  return (
    <div className="mb-5 flex items-center text-sm font-bold text-gray-500">
      <div className="w-[120px] sm:w-[240px] md:w-[150px] lg:w-[180px]">
        {t.analysis.overview.metricesCompareMetric}
      </div>
      <div className="w-[60px] text-right sm:w-[120px] md:w-[60px]">
        {t.analysis.overview.metricesCompareValue}
      </div>
      <div className="ml-8 max-w-[160px] flex-1">
        {t.analysis.overview.metricesCompareVersusIndustry}
      </div>
    </div>
  );
};

const MetricList = ({ symbol, lang }: { symbol: string; lang: Lang }) => {
  const {
    data: financialMetricsComparison,
    isFetching,
    isError,
  } = useFinancialMetricsComparison(symbol);
  const { t } = getTranslations(lang);
  if (isFetching) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorView>
        <p>{t.error.somethingWentWrong}</p>
      </ErrorView>
    );
  }

  const metrics = metriceConfig.map(({ indicator, unit }) => {
    const metricFromApi = financialMetricsComparison?.data.find(
      (m) => m.indicator === indicator
    );
    return {
      ...metricFromApi,
      unit,
    };
  });

  // 將 metrics 切成兩半
  const half = Math.ceil(metrics.length / 2);
  const leftMetrics = metrics.slice(0, half);
  const rightMetrics = metrics.slice(half);
  return (
    <>
      {/* Desktop */}
      <div className="hidden grid-cols-2 divide-x divide-gray-200 md:grid">
        <div className="px-4 xl:px-4 2xl:px-8">
          <MetricListHeader lang={lang} />
          {leftMetrics.map((metric, idx) => (
            <MetricListItem key={idx} metric={metric} />
          ))}
        </div>
        <div className="px-4 xl:px-4 2xl:px-8">
          <MetricListHeader lang={lang} />
          {rightMetrics.map((metric, idx) => (
            <MetricListItem key={idx} metric={metric} />
          ))}
        </div>
      </div>
      {/* Mobile */}
      <div className="block md:hidden">
        <div className="sm:px-1">
          <MetricListHeader lang={lang} />
          {metrics.map((metric, idx) => (
            <MetricListItem key={idx} metric={metric} />
          ))}
        </div>
      </div>
    </>
  );
};

export default MetricList;
